import { useState, useEffect } from 'react';
import { Settings, X, Plus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { ActionList } from '@/components/ActionList';
import { ActionForm } from '@/components/ActionForm';
import { useBreakpoint } from '@/hooks/useMediaQuery';
import { useAppStore } from '@/stores/appStore';
import { useToast } from '@/hooks/useToast';
import { cn } from '@/lib/utils';
import type { Action, CreateActionRequest, UpdateActionRequest } from '@/types';

interface ActionModalProps {
  trigger?: React.ReactNode;
  action?: Action;
  onActionChange?: (action: Action | null) => void;
}

type ModalView = 'list' | 'create' | 'edit';

export function ActionModal({ trigger, onActionChange }: ActionModalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentView, setCurrentView] = useState<ModalView>('list');
  const [editingAction, setEditingAction] = useState<Action | null>(null);
  const { isMobile } = useBreakpoint();
  const { toast } = useToast();
  
  const { 
    actions: { loading },
    fetchActions,
    createAction,
    updateAction,
  } = useAppStore();

  // Load actions when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchActions().catch((error) => {
        console.error('Failed to fetch actions:', error);
        toast({
          variant: 'destructive',
          title: 'Failed to load actions',
          description: 'Please try again later.',
        });
      });
    }
  }, [isOpen, fetchActions, toast]);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      // Reset view and editing state when closing
      setCurrentView('list');
      setEditingAction(null);
      if (onActionChange) {
        onActionChange(null);
      }
    }
  };

  const handleCreateAction = () => {
    setCurrentView('create');
    setEditingAction(null);
  };

  const handleEditAction = (action: Action) => {
    setEditingAction(action);
    setCurrentView('edit');
    if (onActionChange) {
      onActionChange(action);
    }
  };

  const handleBackToList = () => {
    setCurrentView('list');
    setEditingAction(null);
    if (onActionChange) {
      onActionChange(null);
    }
  };

  const handleFormSubmit = async (data: CreateActionRequest | UpdateActionRequest) => {
    try {
      if (currentView === 'create') {
        await createAction(data as CreateActionRequest);
        toast({
          variant: 'default',
          title: 'Action created',
          description: 'Your automated action has been created successfully.',
        });
      } else if (currentView === 'edit' && editingAction) {
        await updateAction(editingAction.id, data as UpdateActionRequest);
        toast({
          variant: 'default',
          title: 'Action updated',
          description: 'Your action has been updated successfully.',
        });
      }
      
      // Return to list view after successful submission
      handleBackToList();
    } catch (error) {
      console.error('Form submission error:', error);
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      toast({
        variant: 'destructive',
        title: currentView === 'create' ? 'Failed to create action' : 'Failed to update action',
        description: errorMessage,
      });
      throw error; // Re-throw to let form handle loading state
    }
  };

  const getModalTitle = () => {
    switch (currentView) {
      case 'create':
        return 'Create New Action';
      case 'edit':
        return 'Edit Action';
      default:
        return 'Action Management';
    }
  };

  const getModalDescription = () => {
    switch (currentView) {
      case 'create':
        return 'Create a new automated action for your warrant files';
      case 'edit':
        return 'Edit your automated action settings';
      default:
        return 'Create and manage automated actions for your warrant files';
    }
  };

  const defaultTrigger = (
    <Button 
      variant="ghost" 
      className="w-full justify-start gap-2" 
      size="sm"
      aria-label="Manage Actions"
    >
      <Settings className="h-4 w-4" />
      Manage Actions
    </Button>
  );

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {trigger || defaultTrigger}
      </DialogTrigger>
      <DialogContent 
        className={cn(
          "max-w-4xl w-full h-[90vh] max-h-[800px] p-0 gap-0",
          isMobile && "max-w-[95vw] h-[95vh]"
        )}
        aria-describedby="action-modal-description"
      >
        <DialogHeader className="px-6 py-4 border-b">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-xl font-semibold">
                {getModalTitle()}
              </DialogTitle>
              <p 
                id="action-modal-description" 
                className="text-sm text-muted-foreground mt-1"
              >
                {getModalDescription()}
              </p>
            </div>
            
            {currentView === 'list' && (
              <Button
                onClick={handleCreateAction}
                size="sm"
                className="flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                New Action
              </Button>
            )}
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-hidden">
          <ScrollArea className="h-full">
            <div className="p-6">
              {currentView === 'list' && (
                <ActionList onEditAction={handleEditAction} />
              )}
              
              {(currentView === 'create' || currentView === 'edit') && (
                <div className="space-y-6">
                  {/* Back button */}
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleBackToList}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      ← Back to Actions
                    </Button>
                  </div>
                  
                  <Separator />
                  
                  {/* Action Form */}
                  <ActionForm
                    action={editingAction || undefined}
                    onSubmit={handleFormSubmit}
                    onCancel={handleBackToList}
                    loading={loading}
                  />
                </div>
              )}
            </div>
          </ScrollArea>
        </div>

        {/* Close button for accessibility */}
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          onClick={() => setIsOpen(false)}
          aria-label="Close modal"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </Button>
      </DialogContent>
    </Dialog>
  );
}